import { FC, useEffect, useMemo, useReducer, useState } from 'react';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Table, Button, Icon, TooltipInfo } from '../';
import IndeterminateCheckbox from './components/IndeterminateCheckbox';
import { useProjectContext } from '../../context/ProjectContext';
import {
  ButtonSize,
  ColumnEnum,
  HomeTypeEnum,
  OwnershipType,
  Icons,
  TableSize,
  TabsType,
  IconSize,
  ButtonColor
} from '@/enums';
import strings, { homeTypes, ownershipTypes } from '../../language/Swedish';
import { AdsTableType } from '../../types/dataTypes';
import { formatNumber, formatPrice, removeStreetNumberFromString } from '../../helpers';
import { SortingType, handleColumnClick } from './helpers';
import { AdsSummaryResponseType } from '../../types/apiTypes';
import { exportToExcelAds } from '@/helpers/exportToExcel';
import useUser from '@/api/queries/useUser';
import { ROLE_TYPES } from '@/constants';
import { useTableCalculations } from '@/context/hooks/useTableCalculations';

const HideButton = styled(Button)`
  ${tw`flex justify-center p-0 w-full h-full`};
`;

const StyledIcon = styled(Icon)`
  transform: rotate(45deg);
`;

const DownloadButton = styled(Button)`
  border: 1px solid;
  ${tw`absolute flex items-center justify-center right-2.5 border-gray-300`};
  top: 9px;
`;

interface Props {
  tableData: AdsTableType[];
  tableSummary?: AdsSummaryResponseType | null;
  size?: TableSize;
  hideClientData?: boolean;
  className?: string;
  report?: boolean;
}

const AdsTable: FC<Props> = ({
  tableData,
  tableSummary,
  size,
  hideClientData,
  className,
  report
}) => {
  const sortedStorageItem = localStorage.getItem('table-sorting') || '';
  const { adsCalculations, selectedAdsTableData } = useTableCalculations();
  const [sorting, setSorting] = useState<SortingType[]>(sortedStorageItem ? JSON.parse(sortedStorageItem) : []);
  const [rowSelection, setRowSelection] = useState<Set<string>>(new Set());
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = localStorage.getItem('ads-column-order');
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem('ads-column-order', JSON.stringify(columnOrder));
  }, [columnOrder]);

  const { data: userData } = useUser();
  const hasExcelButtonRole = userData?.roles.includes(ROLE_TYPES.SALES_REF_LIMIT_250);
  const {
    project: { adsTableIds, adsTableColumns, adsTableReportColumns },
    addExcludedTableIds,
    toggleSelectedTableIds,
  } = useProjectContext();

  const rerender = useReducer(() => ({}), {})[1];

  useEffect(() => {
    if (adsTableIds?.selected) {
      const initialSelection = new Set<string>();
      adsTableIds.selected.forEach((id) => {
        initialSelection.add(id);
      });
      setRowSelection(initialSelection);
    }
  }, [adsTableIds.selected]);

  useEffect(() => {
    rerender();
  }, [rowSelection, sorting, tableData]);

  const toRowSelectionState = (selectedSet: Set<string>): RowSelectionState => {
    const rowSelectionState: RowSelectionState = {};
    selectedSet.forEach((id) => {
      rowSelectionState[id] = true;
    });
    return rowSelectionState;
  };

  useEffect(() => {
    if (sorting.length !== 0) {
      localStorage.setItem('table-sorting', JSON.stringify(sorting));
    }
  }, [sorting]);

  const columns = useMemo<ColumnDef<AdsTableType>[]>(() => {
    const allColumns: ColumnDef<AdsTableType>[] = [
      {
        id: 'index',
        header: ({ table }) => (
          <span
            onClick={() => {
              if (sorting.length !== 0) {
                table.resetSorting();
                localStorage.removeItem('table-sorting');

              }
            }}>
            {sorting.length !== 0 ? (
              <TooltipInfo content={strings.table.resetSorting}>
                <div style={{width: "20px"}}>
                  <Icon size={IconSize.medium} icon={Icons.refresh} />
                </div>
              </TooltipInfo>
            ) : (
              ''
            )}
          </span>
        ),
        cell: ({ row }) => {
          return <div>{row.index + 1}</div>;
        },
      },
      {
        id: 'select',
        enableSorting: false,
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked:
                adsTableIds.selected?.size === table.getRowModel().rows.length,
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: () => {
                const isAllSelected =
                  rowSelection.size === table.getRowModel().rows.length;
                const newSelected = new Set(rowSelection);

                if (isAllSelected) {
                  table.getRowModel().rows.forEach((row) => {
                    toggleSelectedTableIds(TabsType.ads, row.id);
                  });
                } else {
                  table.getRowModel().rows.forEach((row) => {
                    if (!adsTableIds?.selected?.has(row.id)) {
                      toggleSelectedTableIds(TabsType.ads, row.id);
                    }
                  });
                }

                setRowSelection(newSelected);
              },
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="flex w-5 items-center justify-between">
            <IndeterminateCheckbox
              {...{
                checked: adsTableIds?.selected?.has(row.id),
                onChange: () => toggleSelectedTableIds(TabsType.ads, row.id),
              }}
            />
          </div>
        ),
      },
      {
        id: ColumnEnum.street,
        accessorFn: (row) => row.street,
        cell: (info) => {
          if (hideClientData)
            return removeStreetNumberFromString(info.getValue() as string);
          else return info.getValue();
        },
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.street}
          </span>
        ),
        footer: () => (
          selectedAdsTableData?.length === 0 ? (<span>
            {strings.table.footers.distance}
            {tableSummary?.average_distance}m
          </span> ) : ""
        ),
      },
      {
        accessorFn: (row) => row.registeredDate,
        id: ColumnEnum.registeredDate,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.registeredDate}
          </span>
        ),
        footer: () => <span>{adsCalculations?.average_registered_date ? adsCalculations.average_registered_date : tableSummary?.average_registered_date ? tableSummary.average_registered_date : ''}</span>,
      },
      {
        accessorFn: (row) => row.advertisingDays,
        id: ColumnEnum.advertisingDays,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.advertisingDays}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.adAvertisingDays}
            </span>
          </TooltipInfo>
        ),
        footer: () => <span>{adsCalculations?.average_advertising_days ? adsCalculations.average_advertising_days : tableSummary?.average_advertising_days ? tableSummary.average_advertising_days : ''}</span>,
      },
      {
        accessorFn: (row) => row.constructionYear,
        id: ColumnEnum.constructionYear,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.constructionYear}
          </span>
        ),
        footer: () => <span>{adsCalculations?.average_construction_year ? adsCalculations.average_construction_year : tableSummary?.average_construction_year ? tableSummary.average_construction_year : ''}</span>,
      },

      {
        accessorFn: (row) => row.livingArea,
        id: ColumnEnum.livingArea,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.livingArea}
          </span>
        ),
        footer: () => <span>{adsCalculations?.average_living_area ? adsCalculations.average_living_area : tableSummary?.average_living_area ? tableSummary.average_living_area : ''}</span>,
      },
      {
        accessorFn: (row) => row.rooms,
        id: ColumnEnum.rooms,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.rooms}
          </span>
        ),
        footer: () => <span>{adsCalculations?.average_rooms ? adsCalculations.average_rooms : tableSummary?.average_rooms ? tableSummary.average_rooms : ''}</span>,
      },
      {
        accessorFn: (row) => row.floorFloors,
        id: ColumnEnum.floorFloors,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <TooltipInfo content={strings.table.tooltip.floorFloors}>
            <span
              onClick={() =>
                handleColumnClick(setSorting, column.columnDef.id)
              }>
              {strings.table.columns.floorFloors}
            </span>
          </TooltipInfo>
        ),
      },
      {
        accessorFn: (row) => row.plotArea,
        id: ColumnEnum.plotArea,
        cell: (info) => formatNumber(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.plotArea}
          </span>
        ),
        footer: () => <span>{adsCalculations?.average_plot_area ? formatNumber(adsCalculations.average_plot_area) : tableSummary?.average_plot_area ? formatNumber(tableSummary.average_plot_area) : ''}</span>,
      },
      {
        accessorFn: (row) => row.ancillaryArea,
        id: ColumnEnum.ancillaryArea,
        cell: (info) => formatNumber(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.ancillaryArea}
          </span>
        ),
        footer: () => <span>{adsCalculations?.average_ancillary_area ? formatNumber(adsCalculations.average_ancillary_area) : tableSummary?.average_ancillary_area ? formatNumber(tableSummary.average_ancillary_area) : ''}</span>,
      },

      {
        accessorFn: (row) => row.price,
        id: ColumnEnum.price,
        cell: (info) =>
          info.getValue() ? formatPrice(info.getValue() as number) : '',
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.price}
          </span>
        ),
        footer: () => (
          <span>{adsCalculations?.average_price ? formatPrice(adsCalculations.average_price) : tableSummary?.average_price ? formatPrice(tableSummary.average_price) : ''}</span>
        ),
      },
      {
        accessorFn: (row) => row.priceSqm,
        id: ColumnEnum.priceSqm,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.adPriceSqm}
          </span>
        ),
        footer: () => (
          <span>{adsCalculations?.average_price_sqm ? formatPrice(adsCalculations.average_price_sqm) : tableSummary?.average_price_sqm ? formatPrice(tableSummary.average_price_sqm) : ''}</span>
        ),
      },

      {
        accessorFn: (row) => row.monthlyFee,
        id: ColumnEnum.monthlyFee,
        cell: (info) => formatPrice(info.getValue() as number),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.monthlyFee}
          </span>
        ),
        footer: () => (
          <span>{adsCalculations?.average_monthly_fee ? formatPrice(adsCalculations.average_monthly_fee) : tableSummary?.average_monthly_fee ? formatPrice(tableSummary.average_monthly_fee) : ''}</span>
        ),
      },
      {
        accessorFn: (row) => row.homeType,
        id: ColumnEnum.homeType,
        cell: (info) => homeTypes[info.getValue() as HomeTypeEnum],
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.homeType}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.ownershipType,
        id: ColumnEnum.ownershipType,
        cell: (info) => ownershipTypes[info.getValue() as OwnershipType],
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.ownershipType}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.firm,
        id: ColumnEnum.firm,
        cell: (info) => info.getValue(),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.firm}
          </span>
        ),
      },
      {
        accessorFn: (row) => row.pdf,
        id: ColumnEnum.pdf,
        cell: (info) =>
          info.getValue() ? (
            <a
              href={info.getValue() as string}
              target="_blank"
              className="flex gap-2">
              {strings.table.link}
              <StyledIcon icon={Icons.arrowUp} />
            </a>
          ) : (
            <span></span>
          ),
        header: ({ column }) => (
          <span
            onClick={() => handleColumnClick(setSorting, column.columnDef.id)}>
            {strings.table.columns.pdf}
          </span>
        ),
      },
      {
        id: 'hideRow',
        enableSorting: false,
        header: () => (
          <TooltipInfo content={strings.table.tooltip.hide}>
            <span>{strings.table.hide}</span>
          </TooltipInfo>
        ),
        cell: ({ row }) => {
          return (
            <HideButton
              icon={Icons.eye}
              size={ButtonSize.large}
              onClick={() => {
                addExcludedTableIds(TabsType.ads, row.original.id);
              }}
            />
          );
        },
      },
    ];

    const columns = allColumns.filter((column) => {
      if (
        column.id === 'select' ||
        column.id === 'hideRow' ||
        column.id === 'index'
      )
        return true;
      return (report ? adsTableReportColumns : adsTableColumns).find((item) => item.id === column.id)?.isVisible;
    });

    if (size === TableSize.small || size === TableSize.medium) {
      return columns.filter(
        (column) => column.id !== 'select' && column.id !== 'hideRow'
      );
    }

    return columns;
  }, [rowSelection, sorting, adsTableColumns, adsTableReportColumns, size, adsCalculations]);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (
    updaterOrValue
  ) => {
    setRowSelection((prev) => {
      const newSelected = new Set<string>();
      if (typeof updaterOrValue === 'function') {
        const updatedState = updaterOrValue(toRowSelectionState(prev));
        Object.keys(updatedState).forEach((id) => {
          if (updatedState[id]) newSelected.add(id);
        });
      } else {
        Object.keys(updaterOrValue).forEach((id) => {
          if (updaterOrValue[id]) newSelected.add(id);
        });
      }

      Array.from(newSelected).forEach((id) =>
        toggleSelectedTableIds(TabsType.ads, id)
      );

      return newSelected;
    });
  };

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      rowSelection: toRowSelectionState(rowSelection),
      sorting,
      columnOrder,
      pagination: {
        pageIndex: 0,
        pageSize: 250,
      },
    },
    enableRowSelection: true,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: handleRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const hideFooter = tableSummary ? true : false;

  return (
    <Table
      pinSelectColumn={!report}
      table={table}
      sorting={sorting[0]}
      rowSelection={toRowSelectionState(rowSelection)}
      size={size}
      hideFooter={hideFooter}
      className={className}
    >
      <DownloadButton
        toolTip={!hasExcelButtonRole ? strings.table.noExcelExport : undefined}
        color={ButtonColor.primary}
        size={ButtonSize.small}
        disabled={!hasExcelButtonRole}
        onClick={() =>
          exportToExcelAds(tableData, strings.dashboard.excelName)
        }>
        <Icon icon={Icons.excel} size={IconSize.medium} />
      </DownloadButton>
    </Table>
  );
};

export default AdsTable;
